/**
 * Extrai o último evento de rastreio do `tracking_raw` da cotação.
 *
 * O backend já salva e já manda esse campo na listagem (cotacaoListDTO), mas
 * nenhuma tela lia — o sistema sabia onde o pacote estava e descartava.
 *
 * UPS e FedEx são normalizados no backend para o mesmo formato
 * (statusDescription, description, activity, eventTime, raw), com uma
 * diferença: a FedEx traz `location.city` no topo, enquanto na UPS a cidade
 * fica dentro de `raw`. As duas são tratadas aqui.
 */

type EventoBruto = {
  statusDescription?: string;
  description?: string;
  activity?: string;
  eventTime?: string | null;
  dateTime?: string | null;
  location?: { city?: string | null; stateOrProvinceCode?: string | null } | null;
  raw?: {
    location?: {
      address?: {
        city?: string | null;
        stateProvince?: string | null;
        countryCode?: string | null;
      } | null;
    } | null;
  } | null;
};

export type UltimoEvento = {
  descricao: string;
  quando: Date | null;
  local: string | null;
};

const texto = (v?: string | null): string => String(v ?? "").trim();

const dataValida = (v?: string | null): Date | null => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** FedEx expõe location no topo; UPS deixa dentro do raw. */
const extrairLocal = (e: EventoBruto): string | null => {
  const fedex = [texto(e.location?.city), texto(e.location?.stateOrProvinceCode)]
    .filter(Boolean)
    .join(", ");
  if (fedex) return fedex;

  const end = e.raw?.location?.address;
  const ups = [texto(end?.city), texto(end?.stateProvince) || texto(end?.countryCode)]
    .filter(Boolean)
    .join(", ");

  return ups || null;
};

export const extrairUltimoEvento = (trackingRaw: unknown): UltimoEvento | null => {
  if (!trackingRaw || typeof trackingRaw !== "object") return null;

  const e = trackingRaw as EventoBruto;
  const descricao =
    texto(e.description) || texto(e.activity) || texto(e.statusDescription);

  // Sem descrição não há o que mostrar — repetir o status seria ruído.
  if (!descricao) return null;

  return {
    descricao,
    quando: dataValida(e.eventTime) ?? dataValida(e.dateTime),
    local: extrairLocal(e),
  };
};

/**
 * "agora", "há 3 horas", "há 2 dias".
 *
 * Escrito à mão porque o date-fns está nas dependências mas nunca foi
 * importado em nenhuma tela — não vale estrear a biblioteca por duas frases.
 */
export const tempoRelativo = (data: Date | null): string => {
  if (!data) return "";

  const segundos = Math.floor((Date.now() - data.getTime()) / 1000);
  if (segundos < 0) return "agora";
  if (segundos < 60) return "agora";

  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) return `há ${minutos} min`;

  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} ${horas === 1 ? "hora" : "horas"}`;

  const dias = Math.floor(horas / 24);
  if (dias < 30) return `há ${dias} ${dias === 1 ? "dia" : "dias"}`;

  const meses = Math.floor(dias / 30);
  return `há ${meses} ${meses === 1 ? "mês" : "meses"}`;
};

/** Linha pronta para a tabela: "Chegou a Miami, FL · há 2 dias". */
export const resumoUltimoEvento = (trackingRaw: unknown): string | null => {
  const evento = extrairUltimoEvento(trackingRaw);
  if (!evento) return null;

  return [
    evento.descricao,
    evento.local,
    tempoRelativo(evento.quando),
  ]
    .filter(Boolean)
    .join(" · ");
};
